import type { Vec2 } from '../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { hexahedronSolid } from '../fabric/solids';
import { archesOf, intradosY, stationsOf, type ArchBridgeSpec } from './archBridge';

export const ARCH_DRESSING_PROJECTION = 0.12;

/** Physical arch stones follow the vault's actual curve. Ring width and mortar joints are
 * reconstruction estimates; they articulate a masonry arch without changing its clear opening. */
export function buildArchDressings(
  builder: MeshBuilder,
  stone: VertexStyle,
  frame: Frame,
  spec: ArchBridgeSpec
): void {
  const stations = stationsOf(spec);
  for (const arch of archesOf(spec)) {
    const points: Vec2[] = stations
      .filter((s) => s.x >= arch.left && s.x <= arch.right)
      .map((s) => [s.x, intradosY(spec, arch, s.x)]);
    buildCurveDressings(builder, stone, frame, points, spec.width);
  }
}

/** A vault with a different curve can share the same physical ring stones. Points run left to right. */
export function buildCurveDressings(
  builder: MeshBuilder,
  stone: VertexStyle,
  frame: Frame,
  points: readonly Vec2[],
  width: number
): void {
  const normals = points.map((_, i): Vec2 => {
    const a = points[Math.max(0, i - 1)]!,
      b = points[Math.min(points.length - 1, i + 1)]!;
    const dx = b[0] - a[0],
      dy = b[1] - a[1],
      length = Math.hypot(dx, dy);
    return [-dy / length, dx / length];
  });
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!,
      b = points[i]!,
      na = normals[i - 1]!,
      nb = normals[i]!;
    const gap = Math.min(0.035, 0.012 / Math.hypot(b[0] - a[0], b[1] - a[1]));
    const at = (t: number, offset: number): Vec2 => [
      a[0] + (b[0] - a[0]) * t + (na[0] + (nb[0] - na[0]) * t) * offset,
      a[1] + (b[1] - a[1]) * t + (na[1] + (nb[1] - na[1]) * t) * offset,
    ];
    const ring = [at(gap, 0.02), at(1 - gap, 0.02), at(gap, 0.3), at(1 - gap, 0.3)];
    const shade = i % 3 === 0 ? 1.08 : i % 3 === 1 ? 0.91 : 1;
    const style: VertexStyle = {
      ...stone,
      color: stone.color.map((c) => c * shade) as [number, number, number],
    };
    for (const side of [-1, 1]) {
      const half = width / 2;
      const zs =
        side > 0
          ? [half - 0.025, half + ARCH_DRESSING_PROJECTION]
          : [-half - ARCH_DRESSING_PROJECTION, -half + 0.025];
      hexahedronSolid(
        builder,
        style,
        frame,
        zs.flatMap((z) => ring.map(([x, y]) => [x, y, z]))
      );
    }
  }
}
